/**
 * Fetch-Wrapper für alle Vimaana-Apps.
 *
 * - `credentials: "include"` → Session-Cookie `vg_session_<app>` wird mitgeschickt
 * - 401 außerhalb von /auth/* → harter Redirect zu /login
 * - JSON-Parsing automatisch, Fehler als typisierte ApiError
 */

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/**
 * Die Meldung, die ein Mensch lesen soll — aus `ApiError.body.detail`.
 *
 * `ApiError.message` ist bewusst technisch (`HTTP 409 on POST /…`) und taugt
 * nicht für eine Oberfläche. FastAPI legt den verständlichen Satz in
 * `detail`; bei einem Pydantic-Validierungsfehler ist das eine **Liste** von
 * Einzelfehlern, nicht ein String. Wer `err.message` anzeigt, zeigt dem Helfer
 * eine HTTP-Nummer statt der Erklärung, die der Server mitgeschickt hat.
 */
export function fehlerText(e: unknown, fallback = "Hat nicht geklappt — probier es noch mal"): string {
  const rumpf = (e as { body?: unknown } | null)?.body;
  const detail = (rumpf as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  // Strukturierte Antwort mit Klartext daneben, z. B. Terminkonflikte (409).
  const meldung = (detail as { meldung?: unknown } | null)?.meldung;
  if (typeof meldung === "string" && meldung.trim()) return meldung;
  if (Array.isArray(detail)) {
    const erster = detail.find(d => typeof (d as { msg?: unknown })?.msg === "string");
    if (erster) return String((erster as { msg: string }).msg);
  }
  return fallback;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try { parsed = JSON.parse(text); } catch { parsed = text; }
  }
  if (!res.ok) {
    if (res.status === 401 && !path.includes("/auth/")) {
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.replace("/login");
      }
    }
    throw new ApiError(res.status, parsed, `HTTP ${res.status} on ${method} ${path}`);
  }
  return parsed as T;
}

/** Multipart-Upload. Bewusst getrennt von `request`: dort wird jeder Rumpf zu
 *  JSON, und ein FormData muss der Browser selbst kodieren — inklusive der
 *  boundary im Content-Type, die man nicht von Hand setzen darf. */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, { method: "POST", credentials: "include", body: form });
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try { parsed = JSON.parse(text); } catch { parsed = text; }
  }
  if (!res.ok) {
    if (res.status === 401 && typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.replace("/login");
    }
    throw new ApiError(res.status, parsed, `HTTP ${res.status} on POST ${path}`);
  }
  return parsed as T;
}

export const apiGet    = <T>(path: string)                       => request<T>("GET", path);
export const apiPost   = <T>(path: string, body?: unknown)       => request<T>("POST", path, body);
export const apiPatch  = <T>(path: string, body?: unknown)       => request<T>("PATCH", path, body);
/** PUT = die Ressource vollstaendig ersetzen (im Gegensatz zu PATCH, das nur
 *  einzelne Felder fortschreibt). Gebraucht z.B. fuer die Belegaufteilung:
 *  was nicht mitgeschickt wird, soll dort verschwinden. */
export const apiPut    = <T>(path: string, body?: unknown)       => request<T>("PUT", path, body);
export const apiDelete = <T>(path: string)                       => request<T>("DELETE", path);
